import type { CSSProperties, MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { COPY } from '../copy';
import { ONBOARDING_ENABLED } from '../gating/isOnboardingEnabled';
import { useIsHiredFor } from './hiredRequisitions';
import { onboardingPathFor } from './jobLink';

interface OnboardingJobButtonProps {
  /** The job's id: the form is the candidate's form for this requisition */
  requisitionId: string | undefined;
  /** The job's display id: the form opens filled from the candidate's application for it */
  requisitionDisplayId: string | undefined;
  /** The host's look, so the button matches the actions beside it */
  className: string;
  /** The host's inline sizing, where its neighbours take theirs from the tenant (the job details page) */
  style?: CSSProperties;
  /** The host's text, through its own translations like the actions beside it; the fallback copy otherwise */
  label?: string;
  /** Defaults to the build gate; tests pass it explicitly */
  enabled?: boolean;
}

/**
 * The way into the onboarding form from an applied job (My Jobs and the job details page, decision 102): it opens
 * `/onboarding?requisitionId=<id>&requisitionDisplayId=<display id>`. The host gives the look and the text, so the
 * button sits with its neighbours; a click doesn't reach the job card underneath. It shows only on a job the candidate
 * is hired for, by the list of the `HiredRequisitionsProvider` around it (decision 109). Nothing renders when the
 * feature is off, the job isn't in that list, or it lacks either usable id.
 */
export function OnboardingJobButton({ requisitionId, requisitionDisplayId, className, style, label = COPY.jobButton, enabled = ONBOARDING_ENABLED }: OnboardingJobButtonProps) {
  const navigate = useNavigate();
  const hired = useIsHiredFor(requisitionDisplayId);
  const path = onboardingPathFor(requisitionId, requisitionDisplayId);
  if (!enabled || !hired || path === undefined) return null;
  const open = (event: MouseEvent) => {
    event.stopPropagation();
    navigate(path);
  };
  return (
    <button type="button" className={className} style={style} onClick={open}>
      {label}
    </button>
  );
}
